<div class="bom-page p-6">
  <div class="loading-state" *ngIf="loading">
    <mat-spinner diameter="48"></mat-spinner>
    <p>Loading BOM data...</p>
  </div>

  <div *ngIf="!loading" class="bom-content max-w-screen-2xl mx-auto">
    <div class="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div>
        <h1 class="page-title flex items-center gap-3">
          <span class="page-title__icon">
            <mat-icon>inventory_2</mat-icon>
          </span>
          Bill of Materials
        </h1>
        <p class="page-subtitle">Search existing BOMs or create a new one for a project</p>
      </div>

      <div class="header-actions">
        <div class="stat-card">
          <div class="stat-card__icon">
            <mat-icon>layers</mat-icon>
          </div>
          <div>
            <p class="stat-card__label">Total BOMs</p>
            <p class="stat-card__value">{{ totalItems || boms.length || 0 }}</p>
          </div>
        </div>
      </div>
    </div>

    <section class="toolbar-card mb-5" *ngIf="!showBomForm">
      <div class="toolbar-row">
        <div class="toolbar-search">
          <span class="toolbar-search__icon" aria-hidden="true">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </span>
          <input
            type="text"
            class="toolbar-input"
            [(ngModel)]="searchTerm"
            [ngModelOptions]="{ standalone: true }"
            (keyup.enter)="onSearch()"
            placeholder="Search BOMs by code, title, or description..."
          />
        </div>

        <select
          class="toolbar-select"
          [(ngModel)]="statusFilter"
          [ngModelOptions]="{ standalone: true }"
          (change)="onFilterChange()"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <select
          class="toolbar-select toolbar-select--project"
          [(ngModel)]="projectFilter"
          [ngModelOptions]="{ standalone: true }"
          (change)="onFilterChange()"
        >
          <option value="">All projects</option>
          <option *ngFor="let project of projects" [value]="project.id">{{ project.project_name }}</option>
        </select>

        <div class="toolbar-actions">
          <button type="button" class="toolbar-btn-primary" (click)="startNewBom()">
            <mat-icon>add</mat-icon>
            New BOM
          </button>
          <button *ngIf="hasActiveFilters" type="button" class="toolbar-btn-secondary" (click)="clearFilters()">
            Clear
          </button>
        </div>
      </div>
    </section>

    <section class="panel composer-panel mb-5" *ngIf="showBomForm" #bomComposer>
      <div class="composer-header">
        <div>
          <h2 class="panel__title">
            {{ selectedBom ? ('Edit ' + (selectedBom.bom_code || 'BOM')) : 'Create BOM' }}
          </h2>
          <p class="panel__subtitle">
            {{ selectedBom ? 'Update project details and materials' : 'Select a project, then search the catalog to add materials' }}
          </p>
        </div>
        <button type="button" class="toolbar-btn-secondary composer-close" (click)="closeBomForm()">
          <mat-icon>close</mat-icon>
          Close
        </button>
      </div>

      <form [formGroup]="bomForm" (ngSubmit)="selectedBom ? updateBom() : saveBom()">
        <div class="composer-grid">
          <label class="field">
            <span class="field-label">Project <span class="req">*</span></span>
            <select
              class="form-control form-control--select"
              formControlName="project_id"
              [class.form-control--error]="bomForm.get('project_id')?.invalid && bomForm.get('project_id')?.touched"
            >
              <option [ngValue]="''">Select a project</option>
              <option *ngFor="let project of projects" [ngValue]="project.id">
                {{ project.project_name }}
              </option>
            </select>
            <span class="field-error" *ngIf="bomForm.get('project_id')?.hasError('required') && bomForm.get('project_id')?.touched">
              Project is required
            </span>
          </label>

          <label class="field">
            <span class="field-label">BOM code</span>
            <input class="form-control" formControlName="bom_code" placeholder="Auto or custom code">
          </label>

          <label class="field">
            <span class="field-label">Version</span>
            <input class="form-control" type="number" formControlName="version" min="1" step="1">
            <span class="field-error" *ngIf="bomForm.get('version')?.hasError('min') && bomForm.get('version')?.touched">
              Must be at least 1
            </span>
          </label>

          <label class="field">
            <span class="field-label">Status</span>
            <select class="form-control form-control--select" formControlName="status">
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </label>
        </div>

        <label class="field field--full">
          <span class="field-label">Description</span>
          <textarea class="form-control form-control--area" formControlName="description" placeholder="Optional notes for this BOM" rows="3"></textarea>
        </label>

        <div class="materials-block">
          <div class="form-block__header">
            <div>
              <h3 class="form-block__title">Materials</h3>
              <p class="panel__subtitle">{{ materials.length }} item<span *ngIf="materials.length !== 1">s</span> · {{ formatCurrency(calculateBomTotals().total) }}</p>
            </div>
            <div class="materials-header-actions">
              <button type="button" class="ghost-btn" (click)="openMaterialsPanel()">
                <mat-icon>category</mat-icon>
                Catalog
              </button>
              <button type="button" class="ghost-btn" (click)="openTemplatesPanel()">
                <mat-icon>content_copy</mat-icon>
                Templates
              </button>
            </div>
          </div>

          <div class="toolbar-search quick-add">
            <span class="toolbar-search__icon" aria-hidden="true">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </span>
            <input
              class="form-control form-control--search"
              [formControl]="quickAddSearch"
              [matAutocomplete]="quickAddAuto"
              placeholder="Search by code or name to add a material..."
              autocomplete="off"
              (keydown.enter)="$event.preventDefault()"
            />
            <mat-autocomplete
              #quickAddAuto="matAutocomplete"
              [displayWith]="displayMaterialFn"
              (optionSelected)="addMaterialFromCatalog($event.option.value)"
            >
              <mat-option *ngFor="let mat of filterMaterials(quickAddSearch.value); trackBy: trackMaterial" [value]="mat">
                <div class="option-row">
                  <span class="option-label">{{ formatMaterialLabel(mat) }}</span>
                  <span class="option-meta">{{ mat.unit || '' }} {{ mat.unit_cost != null ? '· ' + formatCurrency(mat.unit_cost) : '' }}</span>
                </div>
              </mat-option>
              <mat-option *ngIf="filterMaterials(quickAddSearch.value).length === 0" disabled>
                No materials found
              </mat-option>
            </mat-autocomplete>
          </div>

          <div formArrayName="materials">
            <div *ngIf="materials.length === 0" class="materials-empty">
              <mat-icon>search</mat-icon>
              <p>Search the catalog above to add materials. Selecting an item adds it to this BOM.</p>
            </div>

            <div *ngIf="materials.length > 0" class="materials-table-wrap">
              <table class="materials-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Material</th>
                    <th>Quantity</th>
                    <th>Unit Rate</th>
                    <th>Total</th>
                    <th>Remarks</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let material of materials.controls; let i = index" [formGroupName]="i">
                    <td class="row-num">{{ i + 1 }}</td>
                    <td class="material-cell">
                      <div class="toolbar-search table-search">
                        <span class="toolbar-search__icon" aria-hidden="true">
                          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                          </svg>
                        </span>
                        <input
                          class="form-control form-control--search form-control--table"
                          formControlName="material_search"
                          [matAutocomplete]="materialAuto"
                          placeholder="Search material..."
                          autocomplete="off"
                          (input)="onMaterialSearchTyped(i, $event)"
                        />
                        <mat-autocomplete
                          #materialAuto="matAutocomplete"
                          [displayWith]="displayMaterialFn"
                          (optionSelected)="onMaterialSelect(i, $event.option.value)"
                        >
                          <mat-option *ngFor="let mat of filterMaterials(material.get('material_search')?.value); trackBy: trackMaterial" [value]="mat">
                            {{ formatMaterialLabel(mat) }}
                          </mat-option>
                          <mat-option *ngIf="filterMaterials(material.get('material_search')?.value).length === 0" disabled>
                            No materials found
                          </mat-option>
                        </mat-autocomplete>
                      </div>
                    </td>
                    <td>
                      <input class="form-control form-control--table" type="number" formControlName="quantity" min="0.001" step="0.001">
                    </td>
                    <td>
                      <div class="rate-field">
                        <span class="rate-field__prefix">₹</span>
                        <input class="form-control form-control--table" type="number" formControlName="unit_rate" min="0" step="0.01">
                      </div>
                    </td>
                    <td class="amount-cell">{{ formatCurrency(calculateMaterialTotal(material.value)) }}</td>
                    <td>
                      <input class="form-control form-control--table" formControlName="remarks" placeholder="Remarks">
                    </td>
                    <td class="action-cell">
                      <button mat-icon-button color="warn" type="button" (click)="removeMaterial(i)" matTooltip="Remove">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="bom-totals" *ngIf="materials.length > 0">
            <div class="bom-totals__row">
              <span>Subtotal</span>
              <span>{{ formatCurrency(calculateBomTotals().subtotal) }}</span>
            </div>
            <div class="bom-totals__row bom-totals__row--total">
              <span>Total Amount</span>
              <span>{{ formatCurrency(calculateBomTotals().total) }}</span>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="button" class="toolbar-btn-secondary" (click)="closeBomForm()">Cancel</button>
          <button type="submit" class="toolbar-btn-primary" [disabled]="isSubmitting">
            {{ isSubmitting ? (selectedBom ? 'Updating...' : 'Saving...') : (selectedBom ? 'Update BOM' : 'Save BOM') }}
          </button>
        </div>
      </form>
    </section>

    <section class="panel" *ngIf="!showBomForm">
      <div class="panel__header-row">
        <div>
          <h2 class="panel__title">All BOMs</h2>
          <p class="panel__subtitle">
            {{ totalItems || boms.length || 0 }} BOM<span *ngIf="(totalItems || boms.length || 0) !== 1">s</span> found
          </p>
        </div>
      </div>

      <div class="bom-grid" *ngIf="boms.length > 0; else emptyState">
        <article *ngFor="let bom of boms" class="bom-card" [ngClass]="getStatusClass(bom.status)">
          <div class="bom-card__top">
            <div>
              <h3 class="bom-card__code">{{ bom.bom_code }}</h3>
              <div class="bom-card__meta">
                <span>{{ bom.project_name }}</span>
                <span class="bom-card__version">v{{ bom.version }}</span>
              </div>
            </div>
            <span class="status-pill" [ngClass]="getStatusClass(bom.status)">
              <mat-icon>{{ getStatusIcon(bom.status) }}</mat-icon>
              {{ bom.status }}
            </span>
          </div>

          <p class="bom-card__desc" *ngIf="bom.description">{{ bom.description }}</p>

          <div class="bom-card__stats">
            <div>
              <span class="label">Materials</span>
              <span class="value">{{ bom.material_count }}</span>
            </div>
            <div>
              <span class="label">Total</span>
              <span class="value">{{ formatCurrency(bom.total_amount) }}</span>
            </div>
            <div>
              <span class="label">Created</span>
              <span class="value">{{ formatDate(bom.creation_date) }}</span>
            </div>
          </div>

          <div class="bom-card__actions">
            <button mat-stroked-button type="button" (click)="loadBomForEdit(bom)">
              <mat-icon>edit</mat-icon>
              Edit
            </button>
            <button mat-stroked-button type="button" (click)="exportBom(bom, 'json')">
              <mat-icon>download</mat-icon>
              Export
            </button>
            <button mat-stroked-button type="button" (click)="approveBom(bom)" *ngIf="bom.status === 'draft'">
              <mat-icon>approval</mat-icon>
              Approve
            </button>
            <button mat-stroked-button color="warn" type="button" (click)="deleteBom(bom)">
              <mat-icon>delete</mat-icon>
              Delete
            </button>
          </div>
        </article>
      </div>

      <div class="panel__footer" *ngIf="totalPages > 1">
        <mat-paginator
          [length]="totalItems"
          [pageSize]="itemsPerPage"
          [pageIndex]="currentPage - 1"
          [pageSizeOptions]="[5, 10, 20, 50]"
          (page)="onPageChange($event.pageIndex + 1)">
        </mat-paginator>
      </div>
    </section>
  </div>

  <div class="side-panel-backdrop" *ngIf="showMaterialsPanel || showTemplatesPanel" (click)="closeSidePanels()"></div>

  <aside class="side-panel" *ngIf="showMaterialsPanel">
    <div class="side-panel__header">
      <h3>Material catalog</h3>
      <button mat-icon-button type="button" (click)="showMaterialsPanel = false">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="side-panel__body">
      <div class="toolbar-search">
        <span class="toolbar-search__icon" aria-hidden="true">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
        </span>
        <input
          class="form-control form-control--search"
          [(ngModel)]="materialCatalogSearch"
          [ngModelOptions]="{ standalone: true }"
          placeholder="Search materials..."
        />
      </div>
      <div *ngIf="filterMaterials(materialCatalogSearch).length === 0" class="side-panel__empty">
        No materials match your search.
      </div>
      <div *ngFor="let material of filterMaterials(materialCatalogSearch); trackBy: trackMaterial" class="side-panel__item">
        <div>
          <strong>{{ material.system_code || material.code }}</strong>
          <p>{{ material.name }}</p>
          <small>{{ material.category || material['category_name'] }}</small>
        </div>
        <div class="side-panel__item-meta">
          <span>{{ material.unit }}</span>
          <span>{{ formatCurrency(material.unit_cost || 0) }}</span>
          <button type="button" mat-stroked-button color="primary" (click)="addMaterialFromCatalog(material)">Add</button>
        </div>
      </div>
    </div>
  </aside>

  <aside class="side-panel" *ngIf="showTemplatesPanel">
    <div class="side-panel__header">
      <h3>BOM templates</h3>
      <button mat-icon-button type="button" (click)="showTemplatesPanel = false">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div class="side-panel__body">
      <div *ngIf="!templates.length" class="side-panel__empty">No templates available.</div>
      <div *ngFor="let template of templates" class="side-panel__item">
        <div>
          <strong>{{ template.template_name }}</strong>
          <p>{{ template.description }}</p>
          <small>{{ template.category }}</small>
        </div>
        <button mat-stroked-button type="button" (click)="loadTemplate(template)">
          <mat-icon>file_copy</mat-icon>
          Load
        </button>
      </div>
    </div>
  </aside>

  <ng-template #emptyState>
    <div class="empty-state">
      <mat-icon>inventory_2</mat-icon>
      <h3>{{ hasActiveFilters ? 'No matching BOMs' : 'No BOMs yet' }}</h3>
      <p *ngIf="hasActiveFilters">Try a different search or clear filters.</p>
      <p *ngIf="!hasActiveFilters">Create a BOM, then search the material catalog to add items.</p>
      <button *ngIf="hasActiveFilters" type="button" class="ghost-btn" (click)="clearFilters()">Clear filters</button>
      <button *ngIf="!hasActiveFilters" type="button" class="toolbar-btn toolbar-btn--primary" (click)="startNewBom()">
        <mat-icon>add</mat-icon>
        Create BOM
      </button>
    </div>
  </ng-template>
</div>
